import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { CandidateStatus } from "../../components/UIToolkit/Badges/CandidateStatusChip";
import { NurseRole } from "../../utils/models";
import { validatePhoneNumber } from "../../utils/phoneAndEmailUtils";
import type { HiringMessage, HiringThread, InterviewThread } from "./types";

/** Status-filter value space: the interview statuses plus "NEW" for a not-engaged (null) status. */
export type StatusFilterValue = "NEW" | CandidateStatus;

/** Canonical order the status filter lists its options in. */
export const STATUS_FILTER_ORDER: readonly StatusFilterValue[] = ["NEW", "PENDING", "AT_RISK", "CONFIRMED", "RESCHEDULE", "CANCELLED"] as const;

/** The status-chip i18n label key for a filter value (reused so the filter and chips read the same). */
export const statusFilterLabelKey = (value: StatusFilterValue): string => {
	switch (value) {
		case "PENDING":
			return "candidate.status.chip.pending";
		case "AT_RISK":
			return "candidate.status.chip.at_risk";
		case "CONFIRMED":
			return "candidate.status.chip.confirmed";
		case "RESCHEDULE":
			return "candidate.status.chip.reschedule";
		case "CANCELLED":
			return "candidate.status.chip.cancelled";
		default:
			return "candidate.status.chip.new";
	}
};

/** A thread's status as a filter value — "NEW" when not engaged yet (null status). */
export const threadStatusValue = (status: CandidateStatus | null): StatusFilterValue => status ?? "NEW";

/** The candidate cancellation reasons (the interview_cancel_reason enum), in canonical display order — the
 *  single source shared by the cancel-reason picker, the interview dialog, and the filter list. */
export const CANCEL_REASONS = ["ACCEPTED_ANOTHER_JOB", "LOCATION_SCHEDULE", "NOT_INTERESTED", "OTHER"] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

/** Cancel-reason filter value space: the four reasons plus "NONE" (cancelled but no reason given). */
export type CancelReasonFilterValue = CancelReason | "NONE";

/** Canonical order the cancel-reason filter lists its options in (the reasons, then "NONE"). */
export const CANCEL_REASON_FILTER_ORDER: readonly CancelReasonFilterValue[] = [...CANCEL_REASONS, "NONE"] as const;

/** i18n label key for a cancel-reason filter value ("NONE" → "No reason"; others reuse the chip labels). */
export const cancelReasonFilterLabelKey = (value: CancelReasonFilterValue): string =>
	value === "NONE" ? "candidate.cancel_reason.none" : (cancelReasonLabelKey(value) ?? "candidate.cancel_reason.other");

/**
 * A thread's cancel-reason filter value, or null when the dimension doesn't apply (not cancelled).
 * Cancelled with no recorded reason maps to "NONE" — distinct from a non-cancelled candidate.
 */
export const threadCancelReasonValue = (thread: HiringThread): CancelReasonFilterValue | null =>
	threadStatusValue(thread.status) === "CANCELLED" ? ((thread.cancel_reason as CancelReasonFilterValue | null) ?? "NONE") : null;

/**
 * A message we sent is a hard failure when Twilio marked it failed/undelivered or an error code is set.
 * Only our sends can fail: a candidate's message is already here, so an error code on an inbound row
 * (e.g. 11200, our webhook answering Twilio too slowly) is about our reply path, not the message itself.
 */
export const isFailedMessage = (message: Pick<HiringMessage, "direction" | "status" | "error_code">): boolean => {
	if (message.direction === "inbound") return false;
	return message.error_code != null || ["failed", "undelivered"].includes((message.status ?? "").toLowerCase());
};

/** Normalize a candidate's role string (from the sheet) to its short code for matching system roles.
 * Case/whitespace-insensitive exact match against the NurseRole values; unrecognized roles → null. */
export const normalizeRole = (role: string | null): NurseRole | null => {
	const trimmed = (role ?? "").trim().toLowerCase();
	return (Object.values(NurseRole) as NurseRole[]).find((r) => r.toLowerCase() === trimmed) ?? null;
};

/** i18n key for a cancellation reason value, or null when there is no (recognized) reason. */
export const cancelReasonLabelKey = (reason: string | null): string | null => {
	switch (reason) {
		case "ACCEPTED_ANOTHER_JOB":
			return "candidate.cancel_reason.accepted_another_job";
		case "LOCATION_SCHEDULE":
			return "candidate.cancel_reason.location_schedule";
		case "NOT_INTERESTED":
			return "candidate.cancel_reason.not_interested";
		case "OTHER":
			return "candidate.cancel_reason.other";
		default:
			return null;
	}
};

/** A candidate with an empty/blank phone can't be messaged (the send route 502s otherwise). */
export const isReachable = (phone: string | null | undefined): boolean => !!phone && phone.trim().length > 0;

/** The candidate's first name — templates personalize with the first name only (no last name). */
export const firstNameOf = (fullName: string): string => fullName.trim().split(/\s+/)[0] || fullName;

/**
 * Display a candidate phone as "+1 (XXX) XXX-XXXX" (country code + national grouping), reusing the
 * shared `validatePhoneNumber` (libphonenumber). Falls back to the raw value if it can't be parsed.
 */
export const formatCandidatePhone = (phone: string | null | undefined): string => {
	if (!phone) return "";
	const result = validatePhoneNumber(phone);
	if (result.isValid && result.international && result.national) {
		const countryCode = result.international.split(" ")[0]; // e.g. "+1"
		return `${countryCode} ${result.national}`; // e.g. "+1 (937) 818-5453"
	}
	return phone;
};

/**
 * Human date label for an interview: "Today" / "Tomorrow" / "Yesterday" when close, else a short date.
 * `interviewDate` is a Luxon DateTime (date-only, already zone-adjusted by the response interceptor), or
 * null when the interview isn't scheduled yet → "Not scheduled".
 */
export const formatInterviewDate = (interviewDate: DateTime | null, t: TFunction): string => {
	if (!interviewDate?.isValid) return t("hiring_chat.date.not_scheduled");
	const today = DateTime.now().startOf("day");
	const target = interviewDate.startOf("day");
	const diffDays = Math.round(target.diff(today, "days").days);
	if (diffDays === 0) return t("hiring_chat.date.today");
	if (diffDays === 1) return t("hiring_chat.date.tomorrow");
	if (diffDays === -1) return t("hiring_chat.date.yesterday");
	return interviewDate.toFormat("EEE, MMM d");
};

/**
 * Has the interview already started? Mirrors the bot's own gate (`_interview_passed` in
 * services/hiring_bot/state_machine.py): once the moment has passed the bot ignores every reply, so
 * offering to text the candidate an automated question would be asking something we will discard.
 *
 * A date-only interview (no hour) can never "pass" — same rule as the backend.
 */
export const hasInterviewStarted = (interviewDate: DateTime | null, interviewTime: string | null): boolean => {
	if (!interviewDate?.isValid || !interviewTime) return false;
	const time = DateTime.fromISO(interviewTime);
	if (!time.isValid) return false;
	const moment = interviewDate.set({ hour: time.hour, minute: time.minute, second: 0, millisecond: 0 });
	return moment <= DateTime.now();
};

/** Format a bare "HH:MM:SS" time string to a 12h display ("2:30 PM"), or null when unset. */
export const formatInterviewTime = (interviewTime: string | null): string | null => {
	if (!interviewTime) return null;
	const parsed = DateTime.fromISO(interviewTime);
	return parsed.isValid ? parsed.toFormat("h:mm a") : interviewTime;
};

/** Parse a bare "HH:MM:SS" time string into a Luxon DateTime (today's date) for the time picker. */
export const parseInterviewTime = (interviewTime: string | null): DateTime | null => {
	if (!interviewTime) return null;
	const parsed = DateTime.fromISO(interviewTime);
	return parsed.isValid ? parsed : null;
};

/** Serialize a picked time back to the "HH:MM" the PATCH route expects. */
export const serializeInterviewTime = (value: DateTime | null): string | null => (value ? value.toFormat("HH:mm") : null);

/** Whether the thread has an interview to manage; false for a Hub candidate added without one. */
export const hasInterview = (thread: HiringThread): thread is InterviewThread => thread.interview_id !== null;

/**
 * The thread's stable identity in the list and the URL: its interview, or — for a candidate with no interview yet —
 * its process (one row per interview-less process). Interview and process ids are distinct UUIDs, so they never collide.
 */
export const threadKey = (thread: HiringThread): string => (hasInterview(thread) ? thread.interview_id : thread.process_id);

/** Client-side name search over threads. */
export const matchesSearch = (thread: HiringThread, search: string): boolean => {
	const query = search.trim().toLowerCase();
	if (!query) return true;
	return thread.candidate_name.toLowerCase().includes(query);
};
